import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../context/AuthContext'
import { Nav } from '../components/Nav'

export default function Settings() {
  const { get } = useApi()
  const { logout } = useAuth()

  const [audioStorage, setAudioStorage] = useState(false)

  const handleExportData = async () => {
    try {
      // Export sessions
      const sessionsData = await get<{ sessions: unknown[] }>('/api/sessions?limit=1000')
      const journalData = await get<{ entries: unknown[] }>('/api/journal?limit=1000')

      const exportData = {
        exported_at: new Date().toISOString(),
        sessions: sessionsData.sessions,
        journal_entries: journalData.entries,
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `reframer-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export data:', error)
      alert('Failed to export data. Please try again.')
    }
  }

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete all your data? This cannot be undone.')) {
      if (window.confirm('Really delete everything? All sessions and journal entries will be permanently lost.')) {
        logout()
        alert('Your local data has been cleared.')
      }
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>

      <div className="page-content">
        {/* Privacy */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.75rem' }}>Privacy</h3>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.75rem 0',
            borderBottom: '1px solid var(--color-border)'
          }}>
            <div>
              <p style={{ fontWeight: '500', marginBottom: '0.25rem' }}>Audio Storage</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                Save voice recordings to cloud
              </p>
            </div>
            <button
              onClick={() => setAudioStorage(!audioStorage)}
              style={{
                width: '48px',
                height: '28px',
                borderRadius: '14px',
                border: 'none',
                backgroundColor: audioStorage ? 'var(--color-primary)' : 'var(--color-border)',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background-color 0.2s'
              }}
            >
              <span style={{
                position: 'absolute',
                top: '2px',
                left: audioStorage ? '22px' : '2px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: 'white',
                transition: 'left 0.2s'
              }} />
            </button>
          </div>

          <p style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-muted)',
            marginTop: '0.75rem'
          }}>
            {audioStorage
              ? 'Voice recordings will be stored encrypted in the cloud.'
              : 'Voice recordings are processed in real-time and not saved.'}
          </p>
        </div>

        {/* Data */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.75rem' }}>Your Data</h3>

          <button
            onClick={handleExportData}
            className="btn"
            style={{
              width: '100%',
              marginBottom: '0.5rem',
              backgroundColor: 'var(--color-bg)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)'
            }}
          >
            Export Data (JSON)
          </button>

          <button
            onClick={handleDeleteAccount}
            className="btn"
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              color: 'var(--color-error)',
              border: '1px solid var(--color-error)'
            }}
          >
            Delete All Data
          </button>
        </div>

        {/* Crisis Resources - Link to dedicated page */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Crisis Support</h3>
          <p style={{
            color: 'var(--color-text-muted)',
            fontSize: '0.875rem',
            marginBottom: '1rem'
          }}>
            If you're in crisis or need immediate support, help is available.
          </p>

          <Link
            to="/crisis"
            className="btn"
            style={{
              display: 'block',
              width: '100%',
              backgroundColor: 'var(--color-error)',
              color: 'white',
              textDecoration: 'none',
              textAlign: 'center'
            }}
          >
            Get Help Now
          </Link>
        </div>

        {/* Legal */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.75rem' }}>Legal</h3>

          <Link
            to="/privacy"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem 0',
              borderBottom: '1px solid var(--color-border)',
              color: 'var(--color-text)',
              textDecoration: 'none'
            }}
          >
            <span>Privacy Policy</span>
            <span style={{ color: 'var(--color-text-muted)' }}>→</span>
          </Link>

          <Link
            to="/terms"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem 0',
              color: 'var(--color-text)',
              textDecoration: 'none'
            }}
          >
            <span>Terms of Service</span>
            <span style={{ color: 'var(--color-text-muted)' }}>→</span>
          </Link>
        </div>

        {/* Help & Learn */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.75rem' }}>Learn More</h3>

          <Link
            to="/help"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem 0',
              borderBottom: '1px solid var(--color-border)',
              color: 'var(--color-text)',
              textDecoration: 'none'
            }}
          >
            <div>
              <span>Help & Guide</span>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                Learn about cognitive reframing
              </p>
            </div>
            <span style={{ color: 'var(--color-text-muted)' }}>→</span>
          </Link>
        </div>

        {/* About */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>About PresentLens</h3>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
            PresentLens is a self-help tool for practicing cognitive reframing techniques based on
            evidence-based CBT principles from Aaron Beck and Judith Beck.
          </p>

          <div style={{
            padding: '0.75rem',
            backgroundColor: 'var(--color-bg)',
            borderRadius: '0.5rem',
            borderLeft: '3px solid var(--color-warning)'
          }}>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
              <strong style={{ color: 'var(--color-warning)' }}>Important:</strong> This app is not
              therapy, not a medical device, and does not provide diagnosis or treatment.
              If you're struggling, please seek help from a licensed mental health professional.
            </p>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
          Version 0.1.0
        </p>
      </div>

      <Nav />
    </div>
  )
}
